import React, { useEffect } from "react";
import { rideOptionsByPark } from "../../data/rideOptionsByPark";
import Tooltip from "./Tooltip";
import { toast } from "../../utils/toast";
import { getOptionAvailability, filterAvailableRides } from "../../utils/availabilityRules";

const RideSelector = ({
  field,
  selectedParks,
  selected,
  handleChange,
  formData,
  showAllRides,
  setShowAllRides,
}) => {
  const current = Array.isArray(selected) ? selected : [];
  const rideStrategies = formData?.attractionRideStrategy?.rideStrategy || [];
  const isParkCompletionist = rideStrategies.includes("Park Completionist – Ride everything possible");

  const tripStart =
    formData?.basicInformation?.startDate &&
    !isNaN(new Date(formData.basicInformation.startDate))
      ? new Date(formData.basicInformation.startDate)
      : null;

  const handleRideToggle = (rideName) => {
    const updated = current.includes(rideName)
      ? current.filter((name) => name !== rideName)
      : [...current, rideName];

    handleChange(field.id, updated);
  };

  const groupRides = (rides) =>
    rides.reduce(
      (groups, ride) => {
        const type = ride.type === "ILL" ? "ILL" : "LLMP";
        groups[type].push(ride);
        return groups;
      },
      { ILL: [], LLMP: [] }
    );

  useEffect(() => {
    const ridesInForm = Array.isArray(selected) ? selected : [];
    if (ridesInForm.length === 0) return;

    const { filtered } = filterAvailableRides(ridesInForm, formData);
    if (filtered.length !== ridesInForm.length) {
      handleChange(field.id, filtered);
    }
    // Intentionally depend only on selection + trip start; handleChange/field.id are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedParks, selected, formData?.basicInformation?.startDate]);

  const viewAllRidesStrategy = "All Rides – You Choose!";
  const isViewAllRides = rideStrategies.includes(viewAllRidesStrategy);

  const filterRidesByStrategy = (rides) => {
    if (isParkCompletionist || showAllRides || isViewAllRides || rideStrategies.length === 0) return rides;

    return rides.filter((ride) =>
      ride.rideStrategyNotes?.some((note) => rideStrategies.includes(note))
    );
  };

  const hasStrategy = rideStrategies.length > 0;

  return (
    <div className="space-y-6">
      {!hasStrategy && (
        <p className="text-sm italic text-gray-700">
          Please answer the ride strategy question above to begin selecting your
          must-do rides.
        </p>
      )}

      {hasStrategy && (
        <>
          {isParkCompletionist ? (
            <div className="p-4 mb-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-2">
                <h3 className="text-sm font-semibold text-blue-800">Park Completionist Mode Active</h3>
              </div>
              <p className="mt-2 text-sm text-blue-700">
                All available rides from your selected parks have been automatically added to your must-ride list. 
                This ensures you experience everything possible during your visit!
              </p>
              <p className="mt-1 text-xs text-blue-600">
                Total rides selected: {current.length}
              </p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-700">
                Let the magic begin — pick all your must-ride attractions!
              </p>

              <button
                type="button"
                onClick={() => setShowAllRides(!showAllRides)}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
              >
                {showAllRides ? "Show Strategy Matches Only" : "Show All Rides"}
              </button>
            </>
          )}
        </>
      )}

      {hasStrategy &&
        selectedParks.map((park) => {
          const allRides = rideOptionsByPark[park] || [];
          const filteredRides = filterRidesByStrategy(allRides);

          if (!filteredRides.length) return null;

          const grouped = groupRides(filteredRides);

          return (
            <div key={park} className="p-4 rounded-md border bg-white/80">
              <h3 className="mb-2 text-lg font-bold text-textPrimary">
                {park}
              </h3>

              {Object.entries(grouped).map(([type, rides]) => (
                <div key={`${park}-${type}`} className="mb-4">
                  <p className="mb-2 text-xs font-bold text-gray-600 uppercase">
                    {type === "ILL"
                      ? "Individual Lightning Lane (Pay-per-ride)"
                      : "Lightning Lane Multi-Pass"}
                  </p>

                  <div className="space-y-2">
                    {rides.map((ride) => {
                      const isSelected = current.includes(ride.name);
                      const { available, reason, rule } = getOptionAvailability(ride, {
                        tripStartDate: tripStart,
                        optionType: "ride",
                      });
                      const isDisabled = !available || isParkCompletionist;
                      const closed = rule === "closure";
                      const tooEarly = rule === "opening";

                      return (
                        <label
                          key={ride.name}
                          className={`flex gap-2 items-start text-sm text-textPrimary ${isParkCompletionist ? "opacity-75" : ""}`}
                          aria-disabled={isDisabled}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => !isDisabled && handleRideToggle(ride.name)}
                            className={`mt-1 ${isDisabled ? "opacity-50" : ""}`}
                            aria-disabled={isDisabled}
                            title={!available ? reason : undefined}
                          />
                          <div className="flex flex-col items-start gap-0.5">
                            <div className="flex gap-1 items-center">
                              <span className={isDisabled ? "opacity-50" : ""}>
                                {ride.name}
                              </span>
                              {isParkCompletionist && (
                                <span className="px-1.5 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded">
                                  Auto-selected
                                </span>
                              )}

                              {/* Tooltip via ride.rideInfoNote (informational) */}
                              {ride.rideInfoNote && (
                                <Tooltip message={ride.rideInfoNote}>
                                  <span className="text-textPrimary cursor-pointer text-[10px] border border-textPrimary rounded-full px-1 pb-[1px] leading-none">
                                    ?
                                  </span>
                                </Tooltip>
                              )}

                              {/* Note via ride.warningNote (warning/error) */}
                              {ride.warningNote && (
                                <Tooltip message={ride.warningNote}>
                                  <span className="text-red-600 cursor-pointer text-[10px] border border-red-600 rounded-full px-[0.3rem] pb-[1px] leading-none ml-1">
                                    !
                                  </span>
                                </Tooltip>
                              )}
                            </div>

                            {ride.height && (
                              <span className="text-xs text-gray-500">
                                Height Requirement: {ride.height}
                              </span>
                            )}

                            {!available && closed && (
                              <span className="text-xs text-red-500">
                                This ride may be <strong>closed</strong> during
                                your trip for refurbishment or permanent
                                closure.
                              </span>
                            )}

                            {!available && tooEarly && (
                              <span className="text-xs text-yellow-600">
                                This ride may <strong>not be open yet</strong>{" "}
                                during your trip.
                              </span>
                            )}

                            {!available && !closed && !tooEarly && reason && (
                              <span className="text-xs text-amber-600" title={reason}>
                                {reason}
                              </span>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })}

      {current.length > 0 && (
        <p className="mt-3 text-sm text-gray-600">
          <strong>To review your selections, click Next.</strong>
        </p>
      )}

      {current.length === 0 && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() =>
              toast.error(
                "No rides selected. You must pick at least one to continue."
              )
            }
            className="text-sm text-red-600 underline"
          >
            Why can't I continue?
          </button>
        </div>
      )}
    </div>
  );
};

export default RideSelector;